import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { PillButton } from '@sorare/core/src/atoms/buttons/PillButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconHelpCircle } from '@sorare/core/src/atoms/icons/system/iconHelpCircle.ts';
import { cardPull, fantasy } from '@sorare/core/src/lib/glossary';

import type { CardPullRulesDialog_cardPullConfig } from '#src/components/essence/CardPullExperience/CardPullRulesDialog/__generated__/index.graphql.ts';
import {
  CardPullRulesDialog,
  TabValue,
} from '#src/components/essence/CardPullExperience/CardPullRulesDialog/index.tsx';

import type {
  CardPullRulesButton_cardPullConfig,
  CardPullRulesButton_cardShardsChest,
} from './__generated__/index.graphql.ts';

type Props = {
  cardShardsChest: CardPullRulesButton_cardShardsChest;
  cardPullConfigs: CardPullRulesDialog_cardPullConfig[];
  fullWidth?: boolean;
  pill?: boolean;
  iconOnly?: boolean;
};

export const CardPullRulesButton = ({
  cardShardsChest,
  cardPullConfigs,
  fullWidth,
  pill,
  iconOnly,
}: Props) => {
  const { formatMessage } = useIntl();
  const [open, setOpen] = useState(false);
  const helpIcon = <FontAwesomeIcon icon={iconHelpCircle} />;

  return (
    <>
      <CardPullRulesDialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        initialTab={TabValue.HOW_IT_WORKS}
        cardShardsChest={cardShardsChest}
        cardPullConfigs={cardPullConfigs}
      />
      {pill ? (
        <PillButton
          size="small"
          color="tertiary"
          startIcon={helpIcon}
          aria-label={formatMessage(cardPull.rulesAndEligibility)}
          onClick={() => {
            setOpen(true);
          }}
        >
          {iconOnly ? null : <FormattedMessage {...fantasy.rules} />}
        </PillButton>
      ) : (
        <Button
          startIcon={helpIcon}
          size="small"
          color="quaternary"
          onClick={() => {
            setOpen(true);
          }}
          fullWidth={fullWidth}
        >
          <FormattedMessage {...cardPull.rulesAndEligibility} />
        </Button>
      )}
    </>
  );
};

CardPullRulesButton.fragments = {
  cardPullConfig: gql`
    fragment CardPullRulesButton_cardPullConfig on CardPullConfig {
      ...CardPullRulesDialog_cardPullConfig
    }
    ${CardPullRulesDialog.fragments.cardPullConfig}
  ` as TypedDocumentNode<CardPullRulesButton_cardPullConfig>,
  cardShardsChest: gql`
    fragment CardPullRulesButton_cardShardsChest on CardShardsChest {
      id
      ...CardPullRulesDialog_cardShardsChest
    }
    ${CardPullRulesDialog.fragments.cardShardsChest}
  ` as TypedDocumentNode<CardPullRulesButton_cardShardsChest>,
};
